import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getProfilePictureUrl, getDisplayProgram } from "../utils/utils";
import { getUserIdentifiers, validateUserForOperation } from "../utils/userUtils";
import { convertLocalToUTC } from "../utils/timezoneUtils";
import { useQueryClient } from "react-query";
import { motion } from "framer-motion";
import API_URL from '../apiConfig';
import useEfficientSearch from '../hooks/useEfficientSearch';
import { useActionButtonData } from '../context/ActionButtonDataContext';

function BookingAppointment({ closeModal, role: propRole }) {
  const queryClient = useQueryClient();
  // Get user identification from the utility function
  const { userId, studentId, teacherId, role: userRole } = getUserIdentifiers();
  // Determine role setup - prefer props, then use the utility result
  const role = propRole || userRole || "student";
  const navigate = useNavigate();
  const location = useLocation();

  // IMMEDIATE FIX: Fix case inconsistency with studentId/studentID
  useEffect(() => {
    // This specifically fixes the issue you're seeing
    if (role === "student") {
      const lowercaseId = localStorage.getItem("studentId");
      if (lowercaseId && !localStorage.getItem("studentID")) {
        localStorage.setItem("studentID", lowercaseId);
        console.log("BookingAppointment: Fixed case inconsistency - copied studentId to studentID");
      }
    }
  }, [role]);

  // Add mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  
  // Add resize listener for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Replace full lists with search result state
  const [searchTerm, setSearchTerm] = useState("");
  // --- Modified: selectedStudents now holds full student objects ---
  const [selectedStudents, setSelectedStudents] = useState([]);
  
  // Use efficient search hooks for students and teachers
  const studentSearch = useEfficientSearch('students');
  const teacherSearch = useEfficientSearch('teachers');
  
  // Get prefetch context for monitoring
  const { isPrefetching } = useActionButtonData();

  const [teacherSearchTerm, setTeacherSearchTerm] = useState("");
  const [selectedTeacher, setSelectedTeacher] = useState("");
  const [selectedTeacherName, setSelectedTeacherName] = useState("");
  const [selectedTeacherProfile, setSelectedTeacherProfile] = useState("");

  // Additional teacher states
  const [schedule, setSchedule] = useState("");
  const [venue, setVenue] = useState("");
  const [venueId, setVenueId] = useState("");
  const [venues, setVenues] = useState([]);
  
  const [loadingVenues, setLoadingVenues] = useState(false);
  const [teacherID, setTeacherID] = useState(teacherId || "");
  

  // Student-specific state - FIXED: Check both casing variations
  const [studentID, setStudentID] = useState(
    studentId || 
    localStorage.getItem("studentID") || 
    localStorage.getItem("studentId") || 
    ""
  );

  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const searchTimeout = useRef(null);
  const [isStudentInputFocused, setIsStudentInputFocused] = useState(false);
  const [isTeacherInputFocused, setIsTeacherInputFocused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", content: "" });
  const [activePeriod, setActivePeriod] = useState(null);
  const [SubmitBookingClicked, setSubmitBookingClicked] = useState(false);
  const [CancelClicked, setCancelClicked] = useState(false);
  const [enrollmentMessage, setEnrollmentMessage] = useState("");

  // Fetch active period
  const fetchActivePeriod = async () => {
    try {
      const response = await fetch(`${API_URL}/periods/get_active_period`);
      if (response.ok) {
        const periodData = await response.json();
        setActivePeriod(periodData);
      }
    } catch (error) {
      console.error('Error fetching active period:', error);
    }
  };

  // Fetch active period on component mount
  useEffect(() => {
    fetchActivePeriod();
  }, []);

  // Ensure venues are fetched on component mount
  useEffect(() => {
    console.log('Component mounted, fetching venues...');
    // Small delay to ensure API is ready
    setTimeout(() => {
      fetchAllVenues();
    }, 100);
  }, []);

  // Fetch venues for faculty users
  const fetchVenues = async () => {
    console.log('fetchVenues called with role:', role, 'teacherID:', teacherID);
    
    if (role !== "faculty" || !teacherID) {
      console.log('fetchVenues: Not fetching venues - role:', role, 'teacherID:', teacherID);
      return;
    }

    setLoadingVenues(true);
    try {
      console.log('fetchVenues: Fetching teacher data for ID:', teacherID);
      // Get teacher's department
      const response = await fetch(`${API_URL}/user/get_user?idNumber=${teacherID}`);
      const teacherData = await response.json();
      console.log('fetchVenues: Teacher data:', teacherData);
      
      if (teacherData.department) {
        let departmentId = null;
        
        // Extract department ID from the department field
        if (teacherData.department.startsWith("/departments/")) {
          departmentId = teacherData.department.split("/").pop();
          console.log('fetchVenues: Extracted department ID from URL:', departmentId);
        } else {
          // If it's not a URL, try to find the department by name
          console.log('fetchVenues: Department is not URL, searching by name:', teacherData.department);
          const deptResponse = await fetch(`${API_URL}/departments/get_departments`);
          const departments = await deptResponse.json();
          console.log('fetchVenues: Available departments:', departments);
          const dept = departments.find(d => d.name === teacherData.department);
          if (dept) {
            departmentId = dept.id;
            console.log('fetchVenues: Found department by name:', dept);
          }
        }

        if (departmentId) {
          console.log('fetchVenues: Fetching venues for department ID:', departmentId);
          const venuesResponse = await fetch(`${API_URL}/venues/get_venues_by_department/${departmentId}`);
          const venuesData = await venuesResponse.json();
          console.log('fetchVenues: Department venues data:', venuesData);
          
          if (venuesData && venuesData.length > 0) {
            setVenues(venuesData);
            console.log('fetchVenues: Successfully loaded department venues');
          } else {
            console.log('fetchVenues: No venues found for department, fetching all venues');
            fetchAllVenues();
          }
        } else {
          console.log('fetchVenues: No department ID found, fetching all venues');
          fetchAllVenues();
        }
      } else {
        console.log('fetchVenues: Teacher has no department, fetching all venues');
        fetchAllVenues();
      }
    } catch (error) {
      console.error('Error fetching department venues:', error);
      console.log('fetchVenues: Department fetch failed, fetching all venues');
      fetchAllVenues();
    } finally {
      setLoadingVenues(false);
    }
  };

  // Fetch venues when teacherID changes for faculty users
  useEffect(() => {
    console.log('useEffect for fetchVenues - role:', role, 'teacherID:', teacherID);
    if (role === "faculty") {
      // If we don't have teacherID, try to get it from localStorage or user data
      let currentTeacherID = teacherID;
      if (!currentTeacherID) {
        currentTeacherID = localStorage.getItem("teacherID") || 
                          localStorage.getItem("teacherId") || 
                          localStorage.getItem("facultyID") ||
                          localStorage.getItem("userId");
        console.log('useEffect: Found teacherID from localStorage:', currentTeacherID);
        if (currentTeacherID) {
          setTeacherID(currentTeacherID);
        }
      }
      
      if (currentTeacherID) {
        console.log('useEffect: Calling fetchVenues with teacherID:', currentTeacherID);
        fetchVenues();
      } else {
        console.log('useEffect: No teacherID found, trying fallback venue fetch');
        // Fallback: try to fetch all venues if no teacher ID
        fetchAllVenues();
      }
    } else {
      console.log('useEffect: Not faculty role, trying fallback venue fetch');
      // For non-faculty users, try to fetch all venues
      fetchAllVenues();
    }
  }, [role, teacherID]);

  // Function to fetch all venues from database
  const fetchAllVenues = async () => {
    console.log('fetchAllVenues: Fetching venues from database');
    setLoadingVenues(true);
    try {
      console.log('fetchAllVenues: API_URL:', API_URL);
      const response = await fetch(`${API_URL}/venues/get_venues`);
      console.log('fetchAllVenues: Response status:', response.status);
      console.log('fetchAllVenues: Response ok:', response.ok);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const venuesData = await response.json();
      console.log('fetchAllVenues: Database venues data:', venuesData);
      console.log('fetchAllVenues: Venues count from database:', venuesData.length);
      
      if (venuesData && venuesData.length > 0) {
        setVenues(venuesData);
        console.log('fetchAllVenues: Successfully loaded venues from database');
      } else {
        console.log('fetchAllVenues: No venues found in database, using fallback');
        // Fallback venues if database is empty
        const fallbackVenues = [
          { id: 1, name: 'Room 101', is_available: true },
          { id: 2, name: 'Room 102', is_available: true },
          { id: 3, name: 'Conference Room A', is_available: true },
          { id: 4, name: 'Online Meeting', is_available: true }
        ];
        setVenues(fallbackVenues);
      }
    } catch (error) {
      console.error('Error fetching venues from database:', error);
      console.log('fetchAllVenues: API failed, using fallback venues');
      // Set fallback venues if API fails
      const fallbackVenues = [
        { id: 1, name: 'Room 101', is_available: true },
        { id: 2, name: 'Room 102', is_available: true },
        { id: 3, name: 'Conference Room A', is_available: true },
        { id: 4, name: 'Online Meeting', is_available: true }
      ];
      setVenues(fallbackVenues);
    } finally {
      setLoadingVenues(false);
    }
  };

  // Helper function to get minimum date/time (current moment to allow walk-ins)
  const getMinDateTime = () => {
    const now = new Date();
    // Allow booking at current time for walk-ins
    return now.toISOString().slice(0, 16); // Format for datetime-local input
  };

  // Helper function to get maximum date/time (one month from today)
  const getMaxDateTime = () => {
    const today = new Date();
    const oneMonthFromToday = new Date(today);
    oneMonthFromToday.setMonth(today.getMonth() + 1);
    return oneMonthFromToday.toISOString().slice(0, 16); // Format for datetime-local input
  };

  // Helper function to validate selected date/time
  const isDateTimeValid = (dateTimeString) => {
    if (!dateTimeString) return false;
    const selectedDateTime = new Date(dateTimeString);
    const now = new Date();
    // Allow booking from current time to accommodate walk-ins
    const oneMonthFromToday = new Date(now);
    oneMonthFromToday.setMonth(now.getMonth() + 1);
    
    return selectedDateTime >= now && selectedDateTime <= oneMonthFromToday;
  };

  // Check for ID validation issues on component mount with specific error for studentID case issue
  useEffect(() => {
    const { isValid, errorMessage } = validateUserForOperation("booking");
    if (!isValid) {
      setMessage({
        type: "error",
        content: errorMessage
      });
      
      // Special handling for student case issue
      if (role === "student" && localStorage.getItem("studentId") && !localStorage.getItem("studentID")) {
        localStorage.setItem("studentID", localStorage.getItem("studentId"));
        console.log("Fixed studentID case inconsistency during validation");
      }
    }
  }, []);

  // Efficient search handlers using the new hooks
  const handleStudentSearch = async (term) => {
    if (!term.trim()) {
      setEnrollmentMessage("");
      return;
    }

    try {
      const result = await studentSearch.searchData(term, 0);
      if (result.results.length === 0 && term.length > 2) {
        setEnrollmentMessage("No enrolled students found matching your search.");
      } else {
        setEnrollmentMessage("");
      }
      setHasMore(result.hasMore);
    } catch (error) {
      console.error("Student search error:", error);
      setEnrollmentMessage("Search error. Please try again.");
    }
  };

  const handleTeacherSearch = async (term) => {
    if (!term.trim()) {
      return;
    }

    try {
      console.log("BookingAppointment: Searching for teachers with term:", term);
      const result = await teacherSearch.searchData(term, 0);
      console.log("BookingAppointment: Teacher search result:", result);
      console.log("BookingAppointment: Current teacher search results:", teacherSearch.searchResults);
    } catch (error) {
      console.error("Teacher search error:", error);
    }
  };

  // Handle search input change with debouncing
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setPage(0); // Reset pagination when search term changes
    
    // Debounce the search
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    
    searchTimeout.current = setTimeout(() => {
      handleStudentSearch(value);
    }, 300);
  };

  // Handle teacher search input change with debouncing
  const handleTeacherSearchChange = (e) => {
    const value = e.target.value;
    setTeacherSearchTerm(value);
    setSelectedTeacher("");
    setSelectedTeacherName("");
    setSelectedTeacherProfile("");
    
    // Debounce the search
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    
    searchTimeout.current = setTimeout(() => {
      handleTeacherSearch(value);
    }, 300);
  };

  // Load more results when scrolling
  const loadMore = () => {
    if (hasMore && searchTerm) {
      setPage((prev) => prev + 1);
      studentSearch.searchData(searchTerm, page + 1);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);

  // NEW: Helper function to check if booking quota is exceeded.
  const isBookingOverQuota = () => {
    const selectedCount = selectedStudents.length;
    // For student users, include the booking student in the count.
    return role === "student" ? selectedCount + 1 > 4 : selectedCount > 4;
  };

  // Validation function to check for enrolled students in selection
  const validateStudentSelection = (students) => {
    return students.every(student => student.isEnrolled !== false);
  };

  async function submitBooking() {
    // Add specific logging for this issue
    if (role === "student") {
      console.log("Student booking attempt with IDs:", {
        studentId: localStorage.getItem("studentId"),
        studentID: localStorage.getItem("studentID"),
        componentStateID: studentID
      });
      
      // Final failsafe fix
      if (!studentID && localStorage.getItem("studentId")) {
        const id = localStorage.getItem("studentId");
        setStudentID(id); // Now this will work correctly
        console.log("Last-minute fix: Setting studentID from studentId:", id);
      }
    }
    
    // First validate user IDs
    const { isValid, errorMessage } = validateUserForOperation("booking");
    if (!isValid) {
      setMessage({
        type: "error",
        content: errorMessage
      });
      return;
    }
    
    // Check if all selected students are enrolled
    if (!validateStudentSelection(selectedStudents)) {
      setMessage({
        type: "error",
        content: "Some selected students are not enrolled. Please remove them before proceeding."
      });
      return;
    }

    // Validate that the selected date/time is within the allowed range (current time to one month from today)
    if (!isDateTimeValid(schedule)) {
      setMessage({
        type: "error",
        content: "Please select a date and time from now to within one month."
      });
      return;
    }
    
    if (role === "faculty") {
      // Validate required fields: teacherID, at least one student, schedule, and venue must be provided.
      if (!teacherID || selectedStudents.length === 0 || !schedule || (!venueId && !venue)) {
        setMessage({
          type: "error",
          content: "Please fill in all required fields.",
        });
        return;
      }

      setIsLoading(true);
      // Build payload using the required fields.
      const bookingData = {
        teacherID,
        // --- Modified: extract student IDs from the selected student objects ---
        studentIDs: selectedStudents.map((s) => s.id),
        schedule: convertLocalToUTC(schedule),
        venue_id: venueId === 'others' ? null : venueId,
        venue: venueId === 'others' ? venue : null,
        createdBy: teacherID,
      };
      console.log("Faculty bookingData:", bookingData);
      try {
        const response = await fetch(
          `${API_URL}/bookings/create_booking`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(bookingData),
          }
        );

        if (response.ok) {
          // Force refresh of appointments data
          queryClient.invalidateQueries("studentAppointments");
          queryClient.invalidateQueries("teacherAppointments");
          setMessage({
            type: "success",
            content: "Appointment booked successfully!",
          });
          setTimeout(() => {
            if (typeof closeModal === "function") closeModal();
          }, 1500);
        } else {
          const errorData = await response.json();
          setMessage({
            type: "error",
            content: errorData.error || "Failed to book appointment.",
          });
        }
      } catch (error) {
        console.error("Error booking appointment:", error);
        setMessage({
          type: "error",
          content: "Network error. Please try again.",
        });
      } finally {
        setIsLoading(false);
      }    } else {
      // Validate required fields: selectedTeacher, studentID, and schedule must be provided.
      if (!studentID || !selectedTeacher || !schedule) {
        setMessage({
          type: "error",
          content: "Please fill in all required fields.",
        });
        return;
      }

      setIsLoading(true);
      
      // Create array of student IDs for the booking payload
      const studentIDsArray = [studentID];
      
      // Only add fellow students if any are selected
      if (selectedStudents.length > 0) {
        // Log selected students for debugging
        console.log("Selected fellow students:", selectedStudents);
        // Get all fellow student IDs and add them to the array
        const fellowStudentIds = selectedStudents.map(s => s.id);
        studentIDsArray.push(...fellowStudentIds);
      }
      
      // Build payload with required keys.
      const bookingData = {
        teacherID: selectedTeacher,
        studentIDs: studentIDsArray,
        schedule: convertLocalToUTC(schedule), // Convert local time to UTC
        createdBy: studentID,
      };
      
      console.log("Student booking payload:", bookingData);
      
      try {
        const response = await fetch(
          `${API_URL}/bookings/create_booking`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(bookingData),
          }
        );
        if (response.ok) {
          // Force refresh of appointments data
          queryClient.invalidateQueries("studentAppointments");
          queryClient.invalidateQueries("teacherAppointments");
          setMessage({
            type: "success",
            content: "Appointment request sent successfully!",
          });
          setTimeout(() => {
            if (typeof closeModal === "function") closeModal();
          }, 1500);
        } else {
          const errorData = await response.json();
          setMessage({
            type: "error",
            content: errorData.error || "Failed to request appointment.",
          });
        }
      } catch (error) {
        console.error("Error requesting appointment:", error);
        setMessage({
          type: "error",
          content: "Network error. Please try again.",
        });
      } finally {
        setIsLoading(false);
      }
    }
  }

  // Add this function to show enrollment messages without Material UI
  const renderEnrollmentMessage = () => {
    if (!enrollmentMessage) return null;
    
    return (
      <div className="mt-1 text-sm text-amber-600">
        {enrollmentMessage}
      </div>
    );
  };
  // Import the getDisplayProgram function from utils
  // This is now imported from utils.js at the top of the file

  return (
    <div className="pt-2 sm:pt-4 px-0">
      {role === "faculty" ? (
        <>
          {/* Teacher's Form - Made Responsive */}
          <div className="space-y-4 sm:space-y-6 mb-8 mx-3 sm:mx-6 md:mx-8">
            {/* Student Selection */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Students <span className="text-red-500">*</span> <span className="text-xs text-gray-500">(Only enrolled students can be added)</span>
              </label>
              <div className="min-h-[45px] flex flex-wrap items-center gap-1 sm:gap-2 border-2 border-[#397de2] rounded-lg px-2 sm:px-3 py-2 focus-within:ring-2 focus-within:ring-[#54BEFF]">
                {selectedStudents.map((student) => (
                  <div
                    key={student.id}
                    className="bg-[#397de2] text-white px-1 sm:px-2 py-1 rounded-full flex items-center gap-1 sm:gap-2 text-xs sm:text-sm mb-1"
                  >
                    <img
                      src={getProfilePictureUrl(student.profile_picture, `${student.firstName} ${student.lastName}`)}
                      alt={`${student.firstName} ${student.lastName}`}
                      className="w-4 h-4 sm:w-5 sm:h-5 rounded-full"
                    />
                    <span className="max-w-[100px] sm:max-w-full truncate">
                      {student.firstName} {student.lastName}
                    </span>
                    <button
                      onClick={() =>
                        setSelectedStudents(
                          selectedStudents.filter((s) => s.id !== student.id)
                        )
                      }
                      className="hover:text-red-300 transition-colors"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <input
                  type="text"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onFocus={() => setIsStudentInputFocused(true)}
                  onBlur={() =>
                    setTimeout(() => setIsStudentInputFocused(false), 200)
                  }
                  placeholder={isMobile ? "Search..." : "Search students..."}
                  className="flex-1 min-w-[80px] outline-none bg-transparent text-sm"
                />
              </div>
              {/* Student Search Results Dropdown - Made Responsive */}
              {isStudentInputFocused && (
                <ul className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 sm:max-h-60 overflow-y-auto">
                  {studentSearch.isLoading ? (
                    Array.from({ length: 3 }).map((_, index) => (
                      <li
                        key={index}
                        className="px-2 sm:px-4 py-2 flex items-center gap-2 sm:gap-3 animate-pulse"
                      >
                        <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gray-200 rounded-full"></div>
                        <div className="flex flex-col">
                          <div className="w-24 sm:w-32 h-3 sm:h-4 bg-gray-200 rounded"></div>
                          <div className="w-16 sm:w-24 h-2 sm:h-3 bg-gray-100 rounded mt-1"></div>
                        </div>
                      </li>
                    ))
                  ) : studentSearch.searchResults.length === 0 ? (
                    <li className="px-2 sm:px-4 py-2 text-center text-gray-500 text-xs sm:text-sm">
                      No students found
                    </li>
                  ) : (
                    studentSearch.searchResults
                      .filter((student) =>
                        // Exclude already selected students and the current student by idNumber
                        student.idNumber !== studentID &&
                        !selectedStudents.some((s) => s.id === student.id)
                      )
                      .map((student) => (
                        <li
                          key={student.id}
                          onMouseDown={() => {
                            setSelectedStudents([...selectedStudents, student]);
                            setSearchTerm("");
                          }}
                          className="px-2 sm:px-4 py-2 hover:bg-gray-50 flex items-center gap-2 sm:gap-3 cursor-pointer"
                        >
                          <img
                            src={getProfilePictureUrl(student.profile_picture, `${student.firstName} ${student.lastName}`)}
                            alt={`${student.firstName} ${student.lastName}`}
                            className="w-6 h-6 sm:w-8 sm:h-8 rounded-full"
                          />
                          <div>
                            <div className="font-medium text-xs sm:text-sm">
                              {student.firstName} {student.lastName}
                            </div>
                            <div className="text-xs text-gray-500">
                              ID: {student.idNumber} • {student.year_section || getDisplayProgram(student)}
                            </div>
                          </div>
                        </li>
                      ))
                  )}
                </ul>
              )}
              {/* Add enrollment message display */}
              {renderEnrollmentMessage()}
            </div>

            {/* Schedule Selection - Made Responsive */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Schedule <span className="text-red-500">*</span>
              </label>
              <input
                type="datetime-local"
                value={schedule}
                min={getMinDateTime()}
                max={getMaxDateTime()}
                onChange={(e) => setSchedule(e.target.value)}
                className="w-full border-2 border-[#397de2] rounded-lg px-2 sm:px-3 py-2 sm:py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-[#54BEFF]"
              />
              <p className="text-xs text-gray-500 mt-1">
                You can book appointments from now up to one month ahead to accommodate walk-ins.
              </p>
            </div>

            {/* Venue Selection - Made Responsive */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Venue <span className="text-red-500">*</span>
              </label>
              {loadingVenues ? (
                <div className="w-full border-2 border-gray-200 rounded-lg px-2 sm:px-3 py-2 sm:py-2 text-sm sm:text-base text-gray-500">
                  Loading venues...
                </div>
              ) : (
                <select
                  value={venueId}
                  onChange={(e) => {
                    setVenueId(e.target.value);
                    if (e.target.value === 'others') {
                      setVenue(''); // Clear venue name for custom input
                    } else {
                      setVenue(e.target.selectedOptions[0]?.text || '');
                    }
                  }}
                  className="w-full border-2 border-[#397de2] rounded-lg px-2 sm:px-3 py-2 sm:py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-[#54BEFF]"
                >
                  <option value="">Select a venue</option>
                  {/* Debug: Show venues count */}
                  {console.log('Rendering venues dropdown, venues count:', venues.length, 'venues:', venues)}
                  {/* Available venues first */}
                  {venues
                    .filter(venue => venue.is_available)
                    .map(venue => (
                      <option key={venue.id} value={venue.id}>
                        {venue.name} (Available)
                      </option>
                    ))}
                  {/* Unavailable venues second */}
                  {venues
                    .filter(venue => !venue.is_available)
                    .map(venue => (
                      <option key={venue.id} value={venue.id}>
                        {venue.name} (Unavailable)
                      </option>
                    ))}
                  {/* Others option last */}
                  <option value="others">Others</option>
                </select>
              )}
              {venueId === 'others' && (
                <input
                  type="text"
                  placeholder={isMobile ? "Room number" : "Enter custom venue (e.g., Room 101)"}
                  value={venue}
                  onChange={(e) => setVenue(e.target.value)}
                  className="w-full border-2 border-[#397de2] rounded-lg px-2 sm:px-3 py-2 sm:py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-[#54BEFF] mt-2"
                />
              )}
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Student's Form - Made Responsive */}
          <div className="space-y-4 sm:space-y-6 mb-12 mx-3 sm:mx-6 md:mx-8">
            {/* Teacher Selection */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Teacher <span className="text-red-500">*</span>
              </label>
              <div className="min-h-[45px] flex items-center gap-1 sm:gap-2 border-2 border-[#397de2] rounded-lg px-2 sm:px-3 py-2 focus-within:ring-2 focus-within:ring-[#54BEFF]">
                {selectedTeacher && !teacherSearchTerm ? (
                  <div className="bg-[#397de2] text-white px-2 py-1 rounded-full flex items-center gap-2 text-xs sm:text-sm">
                    <img
                      src={getProfilePictureUrl(selectedTeacherProfile, selectedTeacherName)}
                      alt={selectedTeacherName || 'Teacher'}
                      className="w-4 h-4 sm:w-5 sm:h-5 rounded-full"
                    />
                    <span className="max-w-[120px] sm:max-w-full truncate">{selectedTeacherName}</span>
                    <button
                      onClick={() => {
                        setSelectedTeacher("");
                        setSelectedTeacherName("");
                        setSelectedTeacherProfile("");
                      }}
                      className="hover:text-red-300 transition-colors"
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <input
                    type="text"
                    value={teacherSearchTerm}
                    onChange={handleTeacherSearchChange}
                    onFocus={() => setIsTeacherInputFocused(true)}
                    onBlur={() =>
                      setTimeout(() => setIsTeacherInputFocused(false), 200)
                    }
                    placeholder={isMobile ? "Search..." : "Search for a teacher..."}
                    className="flex-1 outline-none bg-transparent text-sm"
                  />
                )}
              </div>

              {/* Teacher Search Results Dropdown - Made Responsive */}
              {isTeacherInputFocused && (
                <ul className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 sm:max-h-60 overflow-y-auto">
                  {teacherSearch.isLoading ? (
                    Array.from({ length: 3 }).map((_, index) => (
                      <li
                        key={index}
                        className="px-2 sm:px-4 py-2 flex items-center gap-2 sm:gap-3 animate-pulse"
                      >
                        <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gray-200 rounded-full"></div>
                        <div className="flex flex-col">
                          <div className="w-24 sm:w-32 h-3 sm:h-4 bg-gray-200 rounded"></div>
                          <div className="w-16 sm:w-24 h-2 sm:h-3 bg-gray-100 rounded mt-1"></div>
                        </div>
                      </li>
                    ))
                  ) : teacherSearch.searchResults.length === 0 ? (
                    <li className="px-2 sm:px-4 py-2 text-center text-gray-500 text-xs sm:text-sm">
                      No teachers found
                    </li>
                  ) : (
                    teacherSearch.searchResults.map((teacher) => (
                      <li
                        key={teacher.ID}
                        onMouseDown={() => {
                          setSelectedTeacher(teacher.ID);
                          setSelectedTeacherName(teacher.fullName);
                          setSelectedTeacherProfile(teacher.profilePicture);
                          setTeacherSearchTerm("");
                          setIsTeacherInputFocused(false);
                        }}
                        className="px-2 sm:px-4 py-2 hover:bg-gray-50 flex items-center gap-2 sm:gap-3 cursor-pointer"
                      >
                        <img
                          src={getProfilePictureUrl(teacher.profilePicture, teacher.fullName)}
                          alt={teacher.fullName}
                          className="w-6 h-6 sm:w-8 sm:h-8 rounded-full"
                        />
                        <div>
                          <div className="font-medium text-xs sm:text-sm">
                            {teacher.fullName}
                          </div>
                          <div className="text-xs text-gray-500">
                            {teacher.department}
                          </div>
                        </div>
                      </li>
                    ))
                  )}
                </ul>
              )}
            </div>

            {/* Fellow Students Selection (Optional) - Made Responsive */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Fellow Students (Optional) <span className="text-xs text-gray-500">(Only enrolled students can be added)</span>
              </label>
              <div className="min-h-[45px] flex flex-wrap items-center gap-1 sm:gap-2 border-2 border-[#397de2] rounded-lg px-2 sm:px-3 py-2 focus-within:ring-2 focus-within:ring-[#54BEFF]">
                {selectedStudents.map((student) => (
                  <div
                    key={student.id}
                    className="bg-[#397de2] text-white px-1 sm:px-2 py-1 rounded-full flex items-center gap-1 sm:gap-2 text-xs sm:text-sm mb-1"
                  >
                    <img
                      src={getProfilePictureUrl(student.profile_picture, `${student.firstName} ${student.lastName}`)}
                      alt={`${student.firstName} ${student.lastName}`}
                      className="w-4 h-4 sm:w-5 sm:h-5 rounded-full"
                    />
                    <span className="max-w-[100px] sm:max-w-full truncate">
                      {student.firstName} {student.lastName}
                    </span>
                    <button
                      onClick={() =>
                        setSelectedStudents(
                          selectedStudents.filter((s) => s.id !== student.id)
                        )
                      }
                      className="hover:text-red-300 transition-colors"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <input
                  type="text"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onFocus={() => {
                    setIsStudentInputFocused(true);
                    // If we have a search term but no results yet, trigger the search again
                    if (searchTerm && studentSearch.searchResults.length === 0) {
                      handleStudentSearch(searchTerm);
                    }
                  }}
                  onBlur={() =>
                    setTimeout(() => setIsStudentInputFocused(false), 200)
                  }
                  placeholder={isMobile ? "Search..." : "Search fellow students..."}
                  className="flex-1 min-w-[80px] outline-none bg-transparent text-sm"
                />
              </div>

              {/* Fellow Student Search Results Dropdown - Made Responsive */}
              {isStudentInputFocused && role === "student" && (
                <ul className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 sm:max-h-60 overflow-y-auto">
                  {studentSearch.isLoading ? (
                    // 🚀 Loading Skeleton for Fellow Students
                    Array.from({ length: 3 }).map((_, index) => (
                      <li
                        key={index}
                        className="px-2 sm:px-4 py-2 flex items-center gap-2 sm:gap-3 animate-pulse"
                      >
                        <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gray-200 rounded-full"></div>
                        <div className="flex flex-col">
                          <div className="w-24 sm:w-32 h-3 sm:h-4 bg-gray-200 rounded"></div>
                          <div className="w-16 sm:w-24 h-2 sm:h-3 bg-gray-100 rounded mt-1"></div>
                        </div>
                      </li>
                    ))
                  ) : studentSearch.searchResults.length === 0 ? (
                    <li className="px-2 sm:px-4 py-2 text-center text-gray-500 text-xs sm:text-sm">
                      No students found
                    </li>
                  ) : (
                    studentSearch.searchResults
                      .filter((student) =>
                        // Exclude current user by matching idNumber, not numeric id
                        student.idNumber !== studentID &&
                        !selectedStudents.some((s) => s.id === student.id)
                      )
                      .map((student) => (
                        <li
                          key={student.id}
                          onMouseDown={() => {
                            // Log selected student for debugging
                            console.log("Adding fellow student:", student);
                            setSelectedStudents([...selectedStudents, student]);
                            setSearchTerm("");
                          }}
                          className="px-2 sm:px-4 py-2 hover:bg-gray-50 flex items-center gap-2 sm:gap-3 cursor-pointer"
                        >
                          <img
                            src={getProfilePictureUrl(student.profile_picture, `${student.firstName} ${student.lastName}`)}
                            alt={`${student.firstName} ${student.lastName}`}
                            className="w-6 h-6 sm:w-8 sm:h-8 rounded-full"
                          />
                          <div>
                            <div className="font-medium text-xs sm:text-sm">
                              {student.firstName} {student.lastName}
                            </div>
                            <div className="text-xs text-gray-500">
                              ID: {student.idNumber} • {student.year_section || getDisplayProgram(student)}
                            </div>
                          </div>
                        </li>
                      ))
                  )}
                </ul>
              )}
              {/* Add enrollment message display */}
              {renderEnrollmentMessage()}
            </div>

            {/* NEW: Schedule Selection for Student */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Schedule <span className="text-red-500">*</span>
              </label>
              <input
                type="datetime-local"
                value={schedule}
                min={getMinDateTime()}
                max={getMaxDateTime()}
                onChange={(e) => setSchedule(e.target.value)}
                className="w-full border-2 border-[#397de2] rounded-lg px-2 sm:px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-[#54BEFF]"
              />
              <p className="text-xs text-gray-500 mt-1">
                You can book appointments from now up to one month ahead to accommodate walk-ins.
              </p>
            </div>

            {/* Period Display for Student */}
            {activePeriod && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Current Period
                </label>
                <div className="w-full border-2 border-gray-200 rounded-lg px-2 sm:px-3 py-2 text-sm sm:text-base bg-gray-50 text-gray-600">
                  {activePeriod.name}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  This consultation will be recorded under the current active period.
                </p>
              </div>
            )}

          </div>
        </>
      )}

      {/* Message display - Made Responsive */}
      {message.content && (
        <div
          className={`p-2 sm:p-3 rounded-lg text-xs sm:text-sm mb-4 mx-3 sm:mx-6 md:mx-8 ${
            message.type === "success"
              ? "bg-green-100 text-green-700"
              : "bg-red-100 text-red-700"
          }`}
        >
          {message.content}
        </div>
      )}

      {/* Booking quota warning */}
      {isBookingOverQuota() && (
        <div className="text-red-500 text-xs sm:text-sm rounded-lg mb-4 p-2 bg-red-50 mx-3 sm:mx-6 md:mx-8">
          Warning: You have exceeded the maximum number of 4 students per
          consultation.
        </div>
      )}

      {/* Submit Button - Now extends to modal edges naturally */}
      <div className="flex mt-4">
        <button
          onClick={() =>{
            setSubmitBookingClicked(true);
            setTimeout(() =>{ setSubmitBookingClicked(false);
              setTimeout(() => submitBooking(), 500);
            }, 200);
          }}
          disabled={isBookingOverQuota() || isLoading}
          className={`${
            isBookingOverQuota() || isLoading
              ? "opacity-50 cursor-not-allowed"
              : ""
          } bg-[#397de2] hover:bg-[#54BEFF] text-white flex-1 py-3 sm:py-4 rounded-bl-xl justify-center transition-colors flex items-center text-xs sm:text-sm font-medium
          ${SubmitBookingClicked ? "scale-100" : "scale-100"}`}
        >
        {isLoading ? (
            <>
              <svg
                className="animate-spin h-4 w-4 sm:h-5 sm:w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span className="ml-2">Processing...</span>
            </>
          ) : (
            <span>
              {role === "faculty" ? "Book Appointment" : "Request Appointment"}
            </span>
          )}
        </button>

        <button
          onClick={() => {
            setCancelClicked(true);
            setTimeout(() => {
              setCancelClicked(false);
              setTimeout(() => closeModal());
            });
          }}
          className={`flex-1 py-3 sm:py-4 text-gray-700 bg-gray-100 rounded-br-xl hover:bg-gray-200 transition-colors text-xs sm:text-sm font-medium
            ${CancelClicked ? "scale-100" : "scale-100"}`}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export default BookingAppointment;
